import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useMenuStore } from './storeMenu'
import {
  copyTable,
  type CopyTableRequest,
  type CreateTableResult,
  createTable,
  createTableDefinition,
  type CreateTableDefinitionRequest,
  type CreateTableRequest,
  deleteTable,
  fetchTableByKey,
  linkTable,
  saveTableColumnDefinition,
  type SaveTableColumnDefinitionResponse,
  type SaveTableColumnDefinitionRequest,
  toggleColumnIncludeInputFormNew,
  toggleColumnReadOnly,
  toggleColumnSearchable,
  unlinkTable,
  updateTableMetadata,
  type UpdateTableMetadataRequest,
  updateTableAccessRoles,
  type UpdateTableAccessRolesRequest,
  type TableEntry
} from '@/api/endpoints/tableDefinitions'
import { updateColumnLabel as apiUpdateColumnLabel } from '@/api/endpoints/tableColumns'
import type { UpdateColumnDefinitionRequest } from '@/api/endpoints/tableColumns'
import { extractErrorMessage } from '@/helpers/errorHandlingHelpers'
import { useNotificationsStore } from './notificationsStore'

/** Henter HTTP-status fra en axios-feil, eller null hvis det ikke er en HTTP-feil. */
function extractHttpStatus (err: unknown): number | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const status = (err as { response?: { status?: unknown } }).response?.status
    return typeof status === 'number' ? status : null
  }
  return null
}

function normalizeTableRef (tableRef: string): string {
  return String(tableRef ?? '').trim()
}

function resolvePublicIdForFetch (tableRef: string): string | undefined {
  const normalizedRef = normalizeTableRef(tableRef)
  if (!normalizedRef) {
    return undefined
  }
  return normalizedRef
}

function requireTableKeyFromRef (tableRef: string): string {
  const normalized = normalizeTableRef(tableRef)
  if (!normalized) {
    throw new Error('Mangler tableKey/publicId for tabellreferanse.')
  }
  return normalized
}

export const useTableDefinitionsStore = defineStore('tableDefinitions', () => {
  // State
  const tables = ref<Record<string, TableEntry>>({})
  const loading = ref(false)
  const error = ref<string | null>(null)
  // HTTP-status for siste fetch-feil (null når ingen feil / ikke en HTTP-feil).
  // Lar konsumenter mappe feil på status i stedet for å string-matche meldingen.
  const errorStatus = ref<number | null>(null)
  const menuStore = useMenuStore()
  const notificationsStore = useNotificationsStore()

  // Race condition prevention
  const pendingRequests = ref<Record<string, Promise<TableEntry | undefined>>>({})

  // Getters
  const getTableByKey = computed(() => {
    return (tableKey: string) => getCachedTableByRef(tableKey)
  })

  const getTablesBySystemObjectName = computed(() => {
    return (systemObjectName: string) => {
      return Object.values(tables.value).filter(table => table.systemObjectName === systemObjectName)
    }
  })

  // Actions
  function getCachedTableByRef (tableRef: string): TableEntry | undefined {
    const normalized = normalizeTableRef(tableRef)
    if (!normalized) {
      return undefined
    }

    const byPublicId = Object.values(tables.value).find(
      table => String(table.publicId || '').trim() === normalized
    )
    if (byPublicId) {
      return byPublicId
    }
    return undefined
  }

  function resolveTableKeyFromMenuItemKey (menuItemKey: string): string | null {
    const normalizedMenuItemKey = String(menuItemKey || '').trim()
    if (!normalizedMenuItemKey) {
      return null
    }
    const menuItem = menuStore.mapOfSystemObjects?.get(normalizedMenuItemKey)
    const tableKey = String(menuItem?.targetPublicId || '').trim()
    return tableKey || null
  }

  async function fetchTable (
    tableRef: string,
    options?: { force?: boolean; silent?: boolean }
  ): Promise<TableEntry | undefined> {
    const publicId = resolvePublicIdForFetch(tableRef)
    if (!publicId) {
      const message = `Mangler tableKey for tabellreferanse '${normalizeTableRef(tableRef)}'.`
      error.value = message
      errorStatus.value = null
      if (!options?.silent) {
        notificationsStore.addError('Kunne ikke oppdatere listen', message)
      }
      return undefined
    }

    if (options?.force) {
      refreshTable(publicId)
    }

    const cachedTable = getCachedTableByRef(publicId)
    if (cachedTable) {
      error.value = null
      errorStatus.value = null
      return cachedTable
    }

    const pendingKey = `table:${publicId}`
    const existingRequest = pendingRequests.value[pendingKey]
    if (existingRequest !== undefined) {
      return existingRequest
    }

    const requestPromise = (async () => {
      loading.value = true
      error.value = null
      errorStatus.value = null
      try {
        const data = await fetchTableByKey(publicId)
        if (!data) {
          const message = `Fant ikke tabell for publicId '${publicId}'.`
          error.value = message
          errorStatus.value = 404
          if (!options?.silent) {
            notificationsStore.addError('Kunne ikke oppdatere listen', message)
          }
          return undefined
        }

        if (data?.publicId) {
          tables.value[publicId] = data
        }
        return data
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Ukjent feil ved henting av tabell'
        error.value = message
        errorStatus.value = extractHttpStatus(err)
        if (!options?.silent) {
          notificationsStore.addError('Kunne ikke oppdatere listen', message)
        }
        return undefined
      } finally {
        loading.value = false
        delete pendingRequests.value[pendingKey]
      }
    })()
    pendingRequests.value[pendingKey] = requestPromise
    return requestPromise
  }

  function clearTables () {
    tables.value = {}
    error.value = null
  }

  function refreshTable (tableRef: string) {
    const normalizedRef = normalizeTableRef(tableRef)
    if (!normalizedRef) {
      return
    }

    const publicId = String(getCachedTableByRef(normalizedRef)?.publicId || '').trim() || normalizedRef
    if (publicId && tables.value[publicId]) {
      delete tables.value[publicId]
    }

    const keys = new Set<string>([`table:${normalizedRef}`])
    if (publicId) keys.add(`table:${publicId}`)
    for (const key of keys) {
      delete pendingRequests.value[key]
    }
  }

  // POST/PUT/DELETE methods from tableDefinitionStore
  const debugPrefix = '[TableDefinitionsStore]'

  async function saveColumnDefinition (
    request: SaveTableColumnDefinitionRequest,
    tableRef: string
  ): Promise<SaveTableColumnDefinitionResponse> {
    const tableKey = requireTableKeyFromRef(tableRef)
    const result = await saveTableColumnDefinition(tableKey, request)

    // INGEN cache-oppdatering her!
    // Parent-komponenten vil kalle fetchTable() etter success
    // for å hente autoritativ data fra backend
    return result
  }

  /**
   * Oppdaterer kolonne-definisjon med all business logic i store
   * Returnerer { success: boolean, error?: string, numberFormatChanged?: boolean }
   *
   * @param request - UpdateColumnDefinitionRequest (samme type som sendes til backend, ingen mapping)
   * @param isReadOnly - UI-styring (ikke del av request)
   * @param isSearchable - UI-styring (US 2937, ikke del av request)
   * @param originalIsSearchable - opprinnelig søkbarhet (for diff-deteksjon)
   */
  async function updateColumnDefinition (
    tableRef: string,
    request: UpdateColumnDefinitionRequest,
    isReadOnly: boolean,
    originalIsReadOnly: boolean,
    isSearchable = false,
    originalIsSearchable = false
  ): Promise<{ success: boolean; error?: string; numberFormatChanged?: boolean }> {
    try {
      // Validering
      if (!request.primaryFieldType) {
        return { success: false, error: 'Velg primærtype' };
      }

      const hasDefaultValue = request.defaultValue !== null && request.defaultValue !== undefined && (typeof request.defaultValue !== 'string' || request.defaultValue.trim() !== '');
      if (request.nullHandling === 'default' && !hasDefaultValue && !request.defaultFunction) {
        return { success: false, error: 'Standardverdi må settes' };
      }

      // Primary type change håndteres av backend - den vet om det er DDL eller metadata
      // Frontend sender bare request, backend bestemmer hva som skal skje

      // Send request direkte til backend (ingen mapping) - backend håndterer all logikk
      const saveResult = await saveColumnDefinition(request, tableRef);

      // Håndter isReadOnly separat hvis endret (ikke en del av UpdateColumnDefinitionRequest)
      if (isReadOnly !== originalIsReadOnly) {
        await setColumnReadOnly(
          request.columnName ?? '',
          isReadOnly,
          tableRef,
          true // silent = true for å unngå ekstra notifikasjon
        );
      }

      // US 2937: håndter isSearchable separat hvis endret (edit-stien bevarer eksisterende
      // søkbarhet i request-body; skrives via eget toggle-endepunkt, som isReadOnly).
      if (isSearchable !== originalIsSearchable) {
        await setColumnSearchable(
          request.columnName ?? '',
          isSearchable,
          tableRef,
          true // silent = true for å unngå ekstra notifikasjon
        );
      }

      notificationsStore.addSuccess(`Kolonne ${request.columnName ?? ''} er oppdatert`);
      return { success: true, numberFormatChanged: saveResult.numberFormatChanged };
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  }

  async function setColumnReadOnly (
    columnName: string,
    newValue: boolean,
    tableRef: string,
    silent = false
  ): Promise<void> {
    try {
      const tableKey = requireTableKeyFromRef(tableRef)
      await toggleColumnReadOnly({ tableKey, columnName, newValue })

      // Oppdater lokal state etter vellykket API-kall
      const table = getCachedTableByRef(tableKey)
      if (table?.columns) {
        const column = table.columns.find((col: any) => col.columnKey === columnName)
        if (column) {
          column.isReadOnly = newValue
        }
      }
    } catch (error) {
      console.error(`${debugPrefix} setColumnReadOnly failed:`, error)
      throw error
    }
  }

  // US 2937: skru fritekst-søkbarhet av/på per kolonne (speiler setColumnReadOnly).
  async function setColumnSearchable (
    columnName: string,
    newValue: boolean,
    tableRef: string,
    silent = false
  ): Promise<void> {
    try {
      const tableKey = requireTableKeyFromRef(tableRef)
      await toggleColumnSearchable({ tableKey, columnName, newValue })

      // Oppdater lokal state etter vellykket API-kall
      const table = getCachedTableByRef(tableKey)
      if (table?.columns) {
        const column = table.columns.find((col: any) => col.columnKey === columnName)
        if (column) {
          column.isSearchable = newValue
        }
      }
    } catch (error) {
      console.error(`${debugPrefix} setColumnSearchable failed:`, error)
      throw error
    }
  }

  async function toggleColumnIncludeInForm (
    columnName: string,
    newValue: boolean,
    tableRef: string,
    silent = false
  ): Promise<void> {
    try {
      const tableKey = requireTableKeyFromRef(tableRef)
      await toggleColumnIncludeInputFormNew({ tableKey, columnName, newValue })

      // Oppdater lokal state etter vellykket API-kall
      const table = getCachedTableByRef(tableKey)
      if (table?.columns) {
        const column = table.columns.find((col: any) => col.columnKey === columnName)
        if (column) {
          column.includeInputFormNew = newValue
        }
      }
    } catch (error) {
      console.error(`${debugPrefix} toggleColumnIncludeInForm failed:`, error)
      throw error
    }
  }

  async function deleteTableAction (tableRef: string): Promise<boolean> {
    try {
      const tableKey = normalizeTableRef(tableRef)
      if (!tableKey) {
        throw new Error('Mangler tableKey/publicId for sletting av tabell.')
      }
      console.log(`${debugPrefix} deleteTableAction called with tableKey:`, tableKey)
      await deleteTable({ tableKey })
      if (tables.value[tableKey]) {
        delete tables.value[tableKey]
      }
      delete pendingRequests.value[`table:${tableKey}`]
      return true
    } catch (error) {
      console.error(`${debugPrefix} deleteTableAction failed:`, error)
      // Re-throw error så den kan håndteres i komponenten
      throw error
    }
  }

  async function unlinkTableAction (tableKey: string): Promise<boolean> {
    const normalized = normalizeTableRef(tableKey)
    try {
      if (!normalized) {
        throw new Error('Mangler tableKey for avkobling.')
      }
      await unlinkTable({ tableKey: normalized })
      await menuStore.refreshMenusAndTree()
      refreshTable(normalized)
      await fetchTable(normalized, { force: true })
      return true
    } catch (error) {
      console.error(`${debugPrefix} unlinkTableAction failed:`, error)
      notificationsStore.addError('Kunne ikke koble fra tabell', extractErrorMessage(error))
      return false
    }
  }

  async function linkTableAction (menuItemKey: string, tableKey: string): Promise<boolean> {
    const normalized = normalizeTableRef(tableKey)
    const normalizedMenuItemKey = String(menuItemKey || '').trim()
    try {
      if (!normalized || !normalizedMenuItemKey) {
        throw new Error('Mangler tableKey eller menuItemKey for kobling.')
      }
      await linkTable({ menuItemKey: normalizedMenuItemKey, tableKey: normalized })
      await menuStore.refreshMenusAndTree()
      refreshTable(normalized)
      await fetchTable(normalized, { force: true })
      return true
    } catch (error) {
      console.error(`${debugPrefix} linkTableAction failed:`, error)
      return false
    }
  }

  async function CreateTableDefinition (
    dbSchemaName: string,
    dbTableName: string,
    options?: { name?: string; alias?: string; description?: string }
  ): Promise<CreateTableResult> {
    try {
      const request: CreateTableDefinitionRequest = { dbSchemaName, dbTableName, ...options }
      return await createTableDefinition(request)
    } catch (error) {
      console.error(`${debugPrefix} CreateTableDefinition failed:`, error)
      throw error
    }
  }

  async function copyTableAction (
    fromSchemaName: string,
    fromTableName: string,
    toSchemaName: string,
    toTableName: string,
    copyData: boolean,
    menuItemKey?: string | null,
    options?: { name?: string; alias?: string; description?: string }
  ): Promise<CreateTableResult> {
    try {
      const request: CopyTableRequest = { fromSchemaName, fromTableName, toSchemaName, toTableName, copyData, menuItemKey, ...options }
      return await copyTable(request)
    } catch (error) {
      console.error(`${debugPrefix} copyTableAction failed:`, error)
      throw error
    }
  }

  async function createTableAction (
    dbSchemaName: string,
    dbTableName: string,
    columnTypes: CreateTableRequest['columnTypes'] = [],
    menuItemKey?: string | null,
    options?: { name?: string; alias?: string; description?: string }
  ): Promise<CreateTableResult> {
    try {
      const request: CreateTableRequest = { dbSchemaName, dbTableName, columnTypes, menuItemKey, ...options }
      const result = await createTable(request)
      // Oppdater meny etter at tabell er opprettet slik at den vises i menyen
      await menuStore.refreshMenusAndTree()
      return result
    } catch (error) {
      console.error(`${debugPrefix} createTableAction failed:`, error)
      throw error
    }
  }

  async function updateTableMetadataAction (
    tableRef: string,
    payload: UpdateTableMetadataRequest
  ): Promise<boolean> {
    try {
      const tableKey = requireTableKeyFromRef(tableRef)
      await updateTableMetadata(tableKey, payload)
      await fetchTable(tableKey, { force: true })
      return true
    } catch (error) {
      console.error(`${debugPrefix} updateTableMetadataAction failed:`, error)
      notificationsStore.addError('Kunne ikke lagre tabellinfo', extractErrorMessage(error))
      return false
    }
  }

  async function updateTableAccessRolesAction (
    tableRef: string,
    payload: UpdateTableAccessRolesRequest
  ): Promise<boolean> {
    try {
      const tableKey = requireTableKeyFromRef(tableRef)
      await updateTableAccessRoles(tableKey, payload)
      await fetchTable(tableKey, { force: true })
      return true
    } catch (error) {
      console.error(`${debugPrefix} updateTableAccessRolesAction failed:`, error)
      notificationsStore.addError('Kunne ikke lagre tilgangsroller', extractErrorMessage(error))
      return false
    }
  }

  async function updateColumnLabel (
    columnName: string,
    newLabel: string,
    tableRef: string
  ): Promise<void> {
    const tableKey = requireTableKeyFromRef(tableRef)
    // Lagre original verdi for eventuell revert
    const table = getCachedTableByRef(tableKey)
    const existingColumn = table?.columns?.find((col: any) => col.columnKey === columnName)
    const originalLabel = existingColumn?.columnLabel

    try {
      // Oppdater lokal state først for rask respons
      if (existingColumn) {
        existingColumn.columnLabel = newLabel
      }

      // Send til backend via dedikert label-endpoint
      await apiUpdateColumnLabel(tableKey, columnName, newLabel)
      notificationsStore.addSuccess(`Visningsnavn endret til "${newLabel}"`)
    } catch (error) {
      console.error(`${debugPrefix} updateColumnLabel failed:`, error)
      // Revert local state on error
      if (existingColumn && originalLabel !== undefined) {
        existingColumn.columnLabel = originalLabel
      }
      notificationsStore.addError('Feil ved oppdatering av visningsnavn', extractErrorMessage(error))
      throw error
    }
  }

  return {
    // State
    tables,
    loading,
    error,
    errorStatus,

    // Getters
    getTableByKey,
    getTablesBySystemObjectName,
    resolveTableKeyFromMenuItemKey,

    // Fetch methods
    fetchTable,

    // Cache management
    clearTables,
    refreshTable,

    // POST/PUT/DELETE methods
    saveColumnDefinition,
    updateColumnDefinition,
    updateColumnLabel,
    toggleColumnReadOnly: setColumnReadOnly,
    toggleColumnSearchable: setColumnSearchable,
    toggleColumnIncludeInForm,
    deleteTable: deleteTableAction,
    unlinkTable: unlinkTableAction,
    linkTable: linkTableAction,
    CreateTableDefinition,
    copyTable: copyTableAction,
    createTable: createTableAction,
    updateTableMetadata: updateTableMetadataAction,
    updateTableAccessRoles: updateTableAccessRolesAction
  }
})

